import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useLazyFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead,
  SectionHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { money, count, dateInputToMs, msToDateInput } from '@/lib/format'
import { Cur } from '@/lib/currency'

const DAY_MS = 24 * 60 * 60 * 1000

/** Товар склада (контракт GET /v1/inventory/items). */
interface InventoryItem {
  id: string
  name: string
  price: number
  deposit: number
  baseStock: number
  isConsumable: boolean
  isActive: boolean
  updatedAt: number
}

/** Строка разбивки продаж по товару. */
interface SalesItemRow {
  itemId: string
  name: string
  qty: number
  revenue: number
}

/** Строка разбивки продаж по виду движения. */
interface SalesKindRow {
  kind: string
  qty: number
  revenue: number
}

/** Ответ GET /v1/inventory/sales. */
interface SalesReport {
  from: number
  to: number
  byItem: SalesItemRow[]
  byKind: SalesKindRow[]
  totals: { qty: number; revenue: number }
}

/** Человекочитаемые названия видов движений склада. */
const KIND_LABEL: Record<string, string> = {
  issue: 'Выдача',
  return: 'Возврат',
  consume: 'Расход',
  adjust: 'Корректировка'
}

export function Inventory(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Склад"
        lead="Товары и инвентарь клуба: цена, залог для прокатных позиций, остаток и признак расходника. Ниже — отчёт по продажам и движениям склада за выбранный период."
      />
      <ClubGate>
        <ItemsSection />
        <SalesSection />
      </ClubGate>
    </>
  )
}

// ─────────────────────────── Товары ───────────────────────────
function ItemsSection(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<InventoryItem[]>('/v1/inventory/items', query, [query?.clubId])
  const [editing, setEditing] = useState<InventoryItem | 'new' | null>(null)
  const [deleting, setDeleting] = useState<InventoryItem | null>(null)
  const del = useMutate()

  const rows = list.data ?? []

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/inventory/items/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} товар(ов)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый товар
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Товары ещё не заданы. Создайте первый."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Цена</th>
                <th className="num">Залог</th>
                <th className="num">Остаток</th>
                <th>Активен</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((it) => (
                <tr key={it.id}>
                  <td>
                    {it.name}
                    {it.isConsumable && <span className="muted"> · расходник</span>}
                  </td>
                  <td className="num">
                    {money(it.price)} <Cur />
                  </td>
                  <td className="num">
                    {it.deposit > 0 ? (
                      <>
                        {money(it.deposit)} <Cur />
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num">{count(it.baseStock)}</td>
                  <td>
                    {it.isActive ? (
                      <span className="status">
                        <span className="dot dot--on" />
                        да
                      </span>
                    ) : (
                      <span className="status">
                        <span className="dot dot--off" />
                        нет
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(it)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(it)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ItemForm
          item={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить товар"
          message={`Удалить товар «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function ItemForm({
  item,
  query,
  onClose,
  onSaved
}: {
  item: InventoryItem | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = item !== null
  const [name, setName] = useState(item?.name ?? '')
  const [price, setPrice] = useState(item ? String(item.price) : '')
  const [deposit, setDeposit] = useState(item ? String(item.deposit) : '')
  const [baseStock, setBaseStock] = useState(item ? String(item.baseStock) : '')
  const [isConsumable, setIsConsumable] = useState(item?.isConsumable ?? false)
  const [isActive, setIsActive] = useState(item?.isActive ?? true)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название товара.')
    const priceNum = price === '' ? 0 : Number(price)
    if (!Number.isFinite(priceNum) || priceNum < 0)
      return setValidation('Цена должна быть неотрицательным числом.')
    const depositNum = deposit === '' ? 0 : Number(deposit)
    if (!Number.isFinite(depositNum) || depositNum < 0)
      return setValidation('Залог должен быть неотрицательным числом.')
    const stockNum = baseStock === '' ? 0 : Number(baseStock)
    if (!Number.isInteger(stockNum) || stockNum < 0)
      return setValidation('Остаток должен быть целым неотрицательным числом.')
    setValidation(null)

    const body = {
      name: trimmed,
      price: priceNum,
      deposit: depositNum,
      baseStock: stockNum,
      isConsumable,
      isActive
    }

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/inventory/items/${item.id}`, body, { query })
        : api.post('/v1/inventory/items', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить товар' : 'Новый товар'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="inventory-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="inventory-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="i-name" full>
            <Input
              id="i-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Кий прокатный"
              autoFocus
            />
          </Field>
          <Field
            label={
              <>
                Цена, <Cur />
              </>
            }
            htmlFor="i-price"
          >
            <Input
              id="i-price"
              type="number"
              min={0}
              step="any"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field
            label={
              <>
                Залог, <Cur />
              </>
            }
            htmlFor="i-deposit"
          >
            <Input
              id="i-deposit"
              type="number"
              min={0}
              step="any"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field label="Остаток" htmlFor="i-stock">
            <Input
              id="i-stock"
              type="number"
              min={0}
              step="1"
              value={baseStock}
              onChange={(e) => setBaseStock(e.target.value)}
              placeholder="0"
            />
          </Field>

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isConsumable}
                onChange={(e) => setIsConsumable(e.target.checked)}
              />
              Расходник (списывается, а не возвращается)
            </label>
          </div>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Активен (доступен на терминале)
            </label>
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ─────────────────────── Продажи за период ───────────────────────
function SalesSection(): React.JSX.Element {
  const query = useConfigQuery()
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const report = useLazyFetch<SalesReport>()

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault()
    const from = dateInputToMs(fromStr)
    // конец дня «по» включительно
    const to = dateInputToMs(toStr) + DAY_MS - 1
    void report.run('/v1/inventory/sales', {
      from,
      to,
      ...(query ?? {})
    })
  }

  const totals = report.data?.totals

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead title="Продажи за период" />

      <form className="report-form" onSubmit={onSubmit}>
        <Field label="С даты" htmlFor="inv-from">
          <Input
            id="inv-from"
            type="date"
            value={fromStr}
            max={toStr}
            onChange={(e) => setFromStr(e.target.value)}
            required
          />
        </Field>
        <Field label="По дату" htmlFor="inv-to">
          <Input
            id="inv-to"
            type="date"
            value={toStr}
            min={fromStr}
            onChange={(e) => setToStr(e.target.value)}
            required
          />
        </Field>
        <div className="field field--action">
          <Button variant="primary" type="submit" disabled={report.loading}>
            {report.loading ? <span className="spin" /> : null}
            Построить
          </Button>
        </div>
      </form>

      <div style={{ marginTop: 20 }}>
        <AsyncState query={report} loadingLabel="Считаем продажи…" />
        {report.data && totals && (
          <>
            <div className="kpi-grid">
              <div className="kpi">
                <div className="kpi__label">Выручка</div>
                <div className="kpi__value kpi__value--gold">
                  {money(totals.revenue)} <Cur />
                </div>
              </div>
              <div className="kpi">
                <div className="kpi__label">Кол-во</div>
                <div className="kpi__value">{count(totals.qty)}</div>
              </div>
            </div>

            <div className="grid" style={{ marginTop: 20 }}>
              <SalesByItemTable rows={report.data.byItem} />
              <SalesByKindTable rows={report.data.byKind} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function SalesByItemTable({ rows }: { rows: SalesItemRow[] }): React.JSX.Element {
  return (
    <div>
      <SectionHead title="По товарам" meta={`${count(rows.length)} поз.`} />
      {rows.length === 0 ? (
        <EmptyState message="Нет продаж за период." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Кол-во</th>
                <th className="num">Выручка</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.itemId}>
                  <td>{r.name}</td>
                  <td className="num">{count(r.qty)}</td>
                  <td className="num">
                    {money(r.revenue)} <Cur />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SalesByKindTable({ rows }: { rows: SalesKindRow[] }): React.JSX.Element {
  return (
    <div>
      <SectionHead title="По видам движений" meta={`${count(rows.length)} поз.`} />
      {rows.length === 0 ? (
        <EmptyState message="Нет движений за период." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Вид</th>
                <th className="num">Кол-во</th>
                <th className="num">Выручка</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.kind}>
                  <td>{KIND_LABEL[r.kind] ?? r.kind}</td>
                  <td className="num">{count(r.qty)}</td>
                  <td className="num">
                    {money(r.revenue)} <Cur />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
